import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getJob,
  isTwitchSignInRequiredError,
} from '../api/rttsClient';
import type { HighlightJob } from '../api/types';
import { JOB_POLL_CEILING_MS, POLL_INTERVAL_MS } from '../config';

export type PollState =
  | { kind: 'idle' }
  | { kind: 'polling'; job: HighlightJob; overdue?: boolean }
  | { kind: 'awaiting_curation'; job: HighlightJob }
  | { kind: 'delivered'; job: HighlightJob }
  | { kind: 'failed'; job: HighlightJob }
  | { kind: 'reconnect' }
  | { kind: 'error'; message: string };

/** Consecutive transient poll failures before surfacing a hard error. */
const MAX_TRANSIENT_FAILURES = 5;

export function isBackendTerminalStatus(status: string | undefined): boolean {
  return status === 'delivered' || status === 'failed';
}

/**
 * Poll getJob until a TERMINAL backend state (delivered | failed | awaiting_curation).
 * A wall-clock ceiling never means failure — it only marks the job overdue
 * while polling continues. Transient non-200s retry; Twitch reconnect and
 * repeated failures stop the loop.
 * Bump `resumeKey` after curation submit to resume polling through render.
 */
export function useJobPolling(
  jobId: string | null,
  enabled: boolean,
  resumeKey = 0
) {
  const [state, setState] = useState<PollState>({ kind: 'idle' });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  const transientFailuresRef = useRef(0);
  const overdueRef = useRef(false);

  const clearPollInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const clearCeilingTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const clearTimer = useCallback(() => {
    clearPollInterval();
    clearCeilingTimer();
  }, [clearPollInterval, clearCeilingTimer]);

  const tick = useCallback(
    async (id: string) => {
      try {
        const { job } = await getJob(id);
        if (cancelledRef.current) return;
        transientFailuresRef.current = 0;
        if (job.status === 'delivered') {
          clearTimer();
          setState({ kind: 'delivered', job });
          return;
        }
        if (job.status === 'failed') {
          clearTimer();
          setState({ kind: 'failed', job });
          return;
        }
        if (job.status === 'awaiting_curation') {
          clearTimer();
          setState({ kind: 'awaiting_curation', job });
          return;
        }
        setState({
          kind: 'polling',
          job,
          overdue: overdueRef.current,
        });
      } catch (error) {
        if (cancelledRef.current) return;
        if (isTwitchSignInRequiredError(error)) {
          clearTimer();
          setState({ kind: 'reconnect' });
          return;
        }
        // Stay on last known polling state; keep interval alive for long jobs.
        transientFailuresRef.current += 1;
        if (transientFailuresRef.current >= MAX_TRANSIENT_FAILURES) {
          clearTimer();
          setState({
            kind: 'error',
            message: 'Could not check reel progress. Please try again.',
          });
        }
      }
    },
    [clearTimer]
  );

  useEffect(() => {
    cancelledRef.current = false;
    transientFailuresRef.current = 0;
    overdueRef.current = false;
    if (!enabled || !jobId) {
      clearTimer();
      setState({ kind: 'idle' });
      return;
    }

    setState({ kind: 'polling', job: { id: jobId, status: 'downloading' } });
    void tick(jobId);
    intervalRef.current = setInterval(() => {
      void tick(jobId);
    }, POLL_INTERVAL_MS);
    timeoutRef.current = setTimeout(() => {
      if (cancelledRef.current) return;
      overdueRef.current = true;
      // Keep polling. Never treat the ceiling as a backend failure.
      setState((prev) =>
        prev.kind === 'polling' ? { ...prev, overdue: true } : prev
      );
    }, JOB_POLL_CEILING_MS);

    return () => {
      cancelledRef.current = true;
      clearTimer();
    };
  }, [jobId, enabled, resumeKey, tick, clearTimer]);

  return state;
}
